import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faSave,
  faTimes,
  faSearch,
  faFilter,
  faEye,
  faEyeSlash,
  faThumbtack,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faChevronLeft,
  faChevronRight,
  faBullhorn,
  faGraduationCap,
  faCalendarAlt,
  faMoneyBillWave,
  faUserGraduate,
  faBook,
  faInfoCircle,
  faNewspaper,
  faCloudUploadAlt,
  faMagic
} from '@fortawesome/free-solid-svg-icons';
import '../styles/ManajemenPostingan.css';

const ManajemenPostingan = () => {
  // States
  const [postingan, setPostingan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [promptTambahan, setPromptTambahan] = useState('');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategori, setFilterKategori] = useState('');
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0
  });
  
  // Form data
  const [formData, setFormData] = useState({
    id: null,
    judul: '',
    konten: '',
    ringkasan: '',
    gambar: '',
    link_url: '',
    kategori: 'umum',
    target_role: 'public',
    platform: 'all',
    prioritas: 'normal',
    is_pinned: false,
    is_active: true,
    tanggal_mulai: new Date().toISOString().slice(0, 16),
    tanggal_selesai: '',
    icon: 'info',
    warna: '#3b82f6'
  });

  // Options
  const kategoriOptions = [
    { value: 'pengumuman', label: 'Pengumuman', icon: faBullhorn },
    { value: 'berita', label: 'Berita', icon: faNewspaper },
    { value: 'event', label: 'Event', icon: faCalendarAlt },
    { value: 'akademik', label: 'Akademik', icon: faBook },
    { value: 'keuangan', label: 'Keuangan', icon: faMoneyBillWave },
    { value: 'pmb', label: 'PMB', icon: faGraduationCap },
    { value: 'alumni', label: 'Alumni', icon: faUserGraduate },
    { value: 'umum', label: 'Umum', icon: faInfoCircle }
  ];

  const prioritasOptions = [
    { value: 'urgent', label: 'Urgent', color: '#ef4444' },
    { value: 'tinggi', label: 'Tinggi', color: '#f59e0b' },
    { value: 'normal', label: 'Normal', color: '#3b82f6' },
    { value: 'rendah', label: 'Rendah', color: '#6b7280' }
  ];

  const iconOptions = [
    { value: 'info', label: 'Info' },
    { value: 'school', label: 'Pendidikan' },
    { value: 'event', label: 'Kalender' },
    { value: 'money', label: 'Keuangan' },
    { value: 'announcement', label: 'Pengumuman' },
    { value: 'checklist', label: 'Checklist' },
    { value: 'warning', label: 'Peringatan' },
    { value: 'user', label: 'User' },
    { value: 'book', label: 'Buku' }
  ];

  // API URL
  const getApiUrl = () => {
    // Development mode (React dev server on port 3000)
    if (window.location.port === '3000') {
      return 'http://localhost/stikbar_webv2/public';
    }
    // Production mode
    if (window.location.hostname === 'stikesbaramuli.ac.id' || window.location.hostname === 'www.stikesbaramuli.ac.id') {
      return 'https://stikesbaramuli.ac.id';
    }
    return '';
  };

  // Fetch postingan
  const fetchPostingan = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = getApiUrl();
      let url = `${baseUrl}/api/postingan.php?page=${pagination.page}&limit=${pagination.limit}`;
      
      if (filterKategori) {
        url += `&kategori=${filterKategori}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        setPostingan(result.data);
        setPagination(prev => ({
          ...prev,
          ...result.pagination
        }));
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Gagal memuat data postingan');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterKategori, searchTerm]);

  useEffect(() => {
    fetchPostingan();
  }, [fetchPostingan]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: null,
      judul: '',
      konten: '',
      ringkasan: '',
      gambar: '',
      link_url: '',
      kategori: 'umum',
      target_role: 'public',
      platform: 'all',
      prioritas: 'normal',
      is_pinned: false,
      is_active: true,
      tanggal_mulai: new Date().toISOString().slice(0, 16),
      tanggal_selesai: '',
      icon: 'info',
      warna: '#3b82f6'
    });
    setImagePreview(null);
    setEditMode(false);
    setShowForm(false);
  };

  // Open form for new post
  const openNewForm = () => {
    resetForm();
    setImagePreview(null);
    setShowForm(true);
    setEditMode(false);
  };

  // Open form for edit
  const openEditForm = (post) => {
    setFormData({
      id: post.id,
      judul: post.judul || '',
      konten: post.konten || '',
      ringkasan: post.ringkasan || '',
      gambar: post.gambar || '',
      link_url: post.link_url || '',
      kategori: post.kategori || 'umum',
      target_role: post.target_role || 'public',
      platform: post.platform || 'all',
      prioritas: post.prioritas || 'normal',
      is_pinned: post.is_pinned === 1 || post.is_pinned === true,
      is_active: post.is_active === 1 || post.is_active === true,
      tanggal_mulai: post.tanggal_mulai ? post.tanggal_mulai.slice(0, 16) : '',
      tanggal_selesai: post.tanggal_selesai ? post.tanggal_selesai.slice(0, 16) : '',
      icon: post.icon || 'info',
      warna: post.warna || '#3b82f6'
    });
    // Set image preview if exists
    if (post.gambar) {
      const baseUrl = getApiUrl();
      setImagePreview(post.gambar.startsWith('http') ? post.gambar : `${baseUrl}${post.gambar}`);
    } else {
      setImagePreview(null);
    }
    setShowForm(true);
    setEditMode(true);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Tipe file tidak diizinkan. Hanya JPG, PNG, GIF, WEBP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Ukuran file maksimal 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    try {
      setUploading(true);
      const baseUrl = getApiUrl();
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await fetch(`${baseUrl}/api/upload.php`, {
        method: 'POST',
        body: formDataUpload
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          gambar: result.url
        }));
        setSuccess('Gambar berhasil diupload');
      } else {
        setError(result.error);
        setImagePreview(null);
      }
    } catch (err) {
      setError('Gagal mengupload gambar');
      setImagePreview(null);
      console.error('Error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Remove image
  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      gambar: ''
    }));
    setImagePreview(null);
  };

  // Generate konten dengan AI
  const handleGenerateContent = async () => {
    if (!formData.judul.trim()) {
      setError('Harap isi judul terlebih dahulu untuk generate konten');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      
      const baseUrl = getApiUrl();
      const response = await fetch(`${baseUrl}/api/generate.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          judul: formData.judul,
          ringkasan: formData.ringkasan,
          kategori: formData.kategori,
          gambar: formData.gambar,
          prompt_tambahan: promptTambahan
        })
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          konten: result.konten,
          ringkasan: result.ringkasan || prev.ringkasan
        }));
        setSuccess('Konten berhasil di-generate!');
      } else {
        setError(result.error || 'Gagal generate konten');
      }
    } catch (err) {
      setError('Gagal terhubung ke server AI');
      console.error('Error:', err);
    } finally {
      setGenerating(false);
    }
  };

  // Save postingan
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.judul.trim() || !formData.konten.trim()) {
      setError('Judul dan konten wajib diisi!');
      return;
    }
    
    try {
      setSaving(true);
      const baseUrl = getApiUrl();
      const url = `${baseUrl}/api/postingan.php`;
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          is_pinned: formData.is_pinned ? 1 : 0,
          is_active: formData.is_active ? 1 : 0
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccess(result.message);
        resetForm();
        fetchPostingan();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Gagal menyimpan postingan');
      console.error('Error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete postingan
  const handleDelete = async (id) => {
    try {
      setSaving(true);
      const baseUrl = getApiUrl();
      const response = await fetch(`${baseUrl}/api/postingan.php?id=${id}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccess(result.message);
        setDeleteConfirm(null);
        fetchPostingan();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Gagal menghapus postingan');
      console.error('Error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Toggle status
  const toggleStatus = async (post, field) => {
    const updatedPost = {
      ...post,
      [field]: post[field] === 1 ? 0 : 1
    };
    
    try {
      const baseUrl = getApiUrl();
      const response = await fetch(`${baseUrl}/api/postingan.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedPost)
      });
      
      const result = await response.json();
      if (result.success) {
        fetchPostingan();
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get prioritas color
  const getPrioritasColor = (prioritas) => {
    const option = prioritasOptions.find(p => p.value === prioritas);
    return option ? option.color : '#3b82f6';
  };

  return (
    <main className="manajemen-postingan-page">
        {/* Header */}
        <section className="admin-header">
          <div className="container">
            <div className="header-content">
              <div className="header-title">
                <h1>
                  <FontAwesomeIcon icon={faNewspaper} /> Manajemen Postingan
                </h1>
                <p>Kelola informasi dan pengumuman untuk website</p>
              </div>
              <button className="btn btn-primary" onClick={openNewForm}>
                <FontAwesomeIcon icon={faPlus} /> Buat Postingan Baru
              </button>
            </div>
          </div>
        </section>

        {/* Alerts */}
        {(success || error) && (
          <div className="container">
            {success && (
              <div className="alert alert-success">
                <FontAwesomeIcon icon={faCheckCircle} /> {success}
              </div>
            )}
            {error && (
              <div className="alert alert-error">
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}
          </div>
        )}

        {/* Filter & Search */}
        <section className="filter-section">
          <div className="container">
            <div className="filter-bar">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} />
                <input 
                  type="text" 
                  placeholder="Cari postingan..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-box">
                <FontAwesomeIcon icon={faFilter} />
                <select 
                  value={filterKategori} 
                  onChange={(e) => setFilterKategori(e.target.value)}
                >
                  <option value="">Semua Kategori</option>
                  {kategoriOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="total-info">
                Total: <strong>{pagination.total}</strong> postingan
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="content-section">
          <div className="container">
            {loading ? (
              <div className="loading-state">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Memuat data...</span>
              </div>
            ) : postingan.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faNewspaper} />
                <h3>Belum Ada Postingan</h3>
                <p>Klik tombol "Buat Postingan Baru" untuk menambahkan</p>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="table-container">
                  <table className="postingan-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>#</th>
                        <th>Judul</th>
                        <th style={{ width: '120px' }}>Kategori</th>
                        <th style={{ width: '100px' }}>Prioritas</th>
                        <th style={{ width: '80px' }}>Status</th>
                        <th style={{ width: '80px' }}>Pinned</th>
                        <th style={{ width: '140px' }}>Tanggal</th>
                        <th style={{ width: '120px' }}>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {postingan.map((post, index) => (
                        <tr key={post.id}>
                          <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                          <td>
                            <div className="post-title-cell">
                              <span className="post-title">{post.judul}</span>
                              {post.ringkasan && (
                                <span className="post-excerpt">{post.ringkasan.substring(0, 60)}...</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="kategori-badge" style={{ background: post.warna || '#3b82f6' }}>
                              {post.kategori}
                            </span>
                          </td>
                          <td>
                            <span 
                              className="prioritas-badge" 
                              style={{ background: getPrioritasColor(post.prioritas) }}
                            >
                              {post.prioritas}
                            </span>
                          </td>
                          <td>
                            <button 
                              className={`status-toggle ${post.is_active === 1 ? 'active' : 'inactive'}`}
                              onClick={() => toggleStatus(post, 'is_active')}
                              title={post.is_active === 1 ? 'Aktif' : 'Nonaktif'}
                            >
                              <FontAwesomeIcon icon={post.is_active === 1 ? faEye : faEyeSlash} />
                            </button>
                          </td>
                          <td>
                            <button 
                              className={`pin-toggle ${post.is_pinned === 1 ? 'pinned' : ''}`}
                              onClick={() => toggleStatus(post, 'is_pinned')}
                              title={post.is_pinned === 1 ? 'Dipin' : 'Tidak dipin'}
                            >
                              <FontAwesomeIcon icon={faThumbtack} />
                            </button>
                          </td>
                          <td className="date-cell">
                            {formatDate(post.tanggal_mulai)}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="btn-icon btn-edit" 
                                onClick={() => openEditForm(post)}
                                title="Edit"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button 
                                className="btn-icon btn-delete" 
                                onClick={() => setDeleteConfirm(post.id)}
                                title="Hapus"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="pagination">
                    <button 
                      className="pagination-btn"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="pagination-info">
                      Halaman {pagination.page} dari {pagination.total_pages}
                    </span>
                    <button 
                      className="pagination-btn"
                      disabled={pagination.page >= pagination.total_pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Form Modal */}
        {showForm && (
          <div className="modal-overlay" onClick={resetForm}>
            <div className="modal-form" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} />
                  {editMode ? 'Edit Postingan' : 'Buat Postingan Baru'}
                </h2>
                <button className="modal-close" onClick={resetForm}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-body">
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Judul <span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="judul" 
                        value={formData.judul}
                        onChange={handleInputChange}
                        placeholder="Masukkan judul postingan"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Ringkasan</label>
                      <input 
                        type="text" 
                        name="ringkasan" 
                        value={formData.ringkasan}
                        onChange={handleInputChange}
                        placeholder="Ringkasan singkat (opsional)"
                        maxLength={500}
                      />
                    </div>
                  </div>

                  {/* AI Generate Section */}
                  <div className="form-row">
                    <div className="form-group full-width ai-generate-section">
                      <label>
                        <FontAwesomeIcon icon={faMagic} /> Generate Konten dengan AI
                      </label>
                      <div className="ai-generate-container">
                        <input 
                          type="text" 
                          value={promptTambahan}
                          onChange={(e) => setPromptTambahan(e.target.value)}
                          placeholder="Prompt tambahan (opsional): mis. 'buatkan dengan gaya formal', 'tambahkan ajakan mendaftar', dll."
                          className="prompt-input"
                        />
                        <button 
                          type="button" 
                          className="btn-generate"
                          onClick={handleGenerateContent}
                          disabled={generating || !formData.judul.trim()}
                        >
                          {generating ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin /> Generating...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faMagic} /> Generate Konten
                            </>
                          )}
                        </button>
                      </div>
                      <small className="ai-hint">
                        AI akan generate konten berdasarkan judul, ringkasan, kategori, dan gambar yang sudah diisi
                      </small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Konten <span className="required">*</span></label>
                      <textarea 
                        name="konten" 
                        value={formData.konten}
                        onChange={handleInputChange}
                        placeholder="Tulis konten postingan di sini atau gunakan AI Generate..."
                        rows={8}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Kategori</label>
                      <select name="kategori" value={formData.kategori} onChange={handleInputChange}>
                        {kategoriOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Prioritas</label>
                      <select name="prioritas" value={formData.prioritas} onChange={handleInputChange}>
                        {prioritasOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Icon</label>
                      <select name="icon" value={formData.icon} onChange={handleInputChange}>
                        {iconOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Warna</label>
                      <div className="color-input">
                        <input 
                          type="color" 
                          name="warna" 
                          value={formData.warna}
                          onChange={handleInputChange}
                        />
                        <span>{formData.warna}</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Tanggal Mulai</label>
                      <input 
                        type="datetime-local" 
                        name="tanggal_mulai" 
                        value={formData.tanggal_mulai}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Tanggal Selesai (Opsional)</label>
                      <input 
                        type="datetime-local" 
                        name="tanggal_selesai" 
                        value={formData.tanggal_selesai}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Gambar Postingan</label>
                      <div className="image-upload-container">
                        {imagePreview || formData.gambar ? (
                          <div className="image-preview">
                            <img 
                              src={imagePreview ? imagePreview : (formData.gambar.startsWith('http') ? formData.gambar : `${getApiUrl()}${formData.gambar}`)} 
                              alt="Preview" 
                              onError={(e) => {
                                e.target.style.display = 'none';
                                console.error('Image failed to load:', e.target.src);
                              }}
                            />
                            <button 
                              type="button" 
                              className="remove-image-btn"
                              onClick={removeImage}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ) : (
                          <label className="image-upload-box">
                            <input 
                              type="file" 
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              onChange={handleImageUpload}
                              hidden
                            />
                            <div className="upload-placeholder">
                              <FontAwesomeIcon icon={faCloudUploadAlt} />
                              <span>Klik untuk upload gambar</span>
                              <small>JPG, PNG, GIF, WEBP (Max 5MB)</small>
                            </div>
                          </label>
                        )}
                        {uploading && (
                          <div className="upload-loading">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Mengupload...</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Link URL (Opsional)</label>
                      <input 
                        type="url" 
                        name="link_url" 
                        value={formData.link_url}
                        onChange={handleInputChange}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="is_active" 
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark"></span>
                        <FontAwesomeIcon icon={faEye} /> Aktif (tampil di website)
                      </label>
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="is_pinned" 
                          checked={formData.is_pinned}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark"></span>
                        <FontAwesomeIcon icon={faThumbtack} /> Pin ke atas
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-footer">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    <FontAwesomeIcon icon={faTimes} /> Batal
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Menyimpan...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} /> Simpan
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="modal-confirm" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <h3>Hapus Postingan?</h3>
              <p>Anda yakin ingin menghapus postingan ini? Tindakan ini tidak dapat dibatalkan.</p>
              <div className="confirm-buttons">
                <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
                  Batal
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={saving}
                >
                  {saving ? 'Menghapus...' : 'Ya, Hapus'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
  );
};

export default ManajemenPostingan;
